import type { AxiosInstance } from 'axios';
import type {
  MailSignature,
  MailSignatureRequest,
  OutreachContact,
  OutreachContactDetail,
  OutreachMailbox,
  OutreachMessage,
  OutreachNote,
  OutreachNoteRequest,
  OutreachPlaceholder,
  OutreachSuppression,
  OutreachTeamMember,
  OutreachTemplate,
  OutreachDraft,
  OutreachDraftRequest,
  OutreachOrganizerDiff,
  Organizer,
  CreateOutreachContactRequest,
  UpdateOutreachContactRequest,
  CreateOutreachTemplateRequest,
  UpdateOutreachTemplateRequest,
  SendOutreachEmailRequest,
} from '../types';

/**
 * Abrufe der Verwaltung, Bereich Posteingang: Anfragen, Outreach, Vorlagen.
 * Einmal für Web und App; die App ruft sie über `create…Service(api)` mit ihrer eigenen Axios-Instanz.
 * Typen, die es in `types.ts` noch nicht gibt, stehen hier im Bereich.
 *
 * Pfade und Parameter wie im Web (`frontend/src/services/inquiryService.ts`,
 * `outreachService.ts`); alle Endpunkte stehen ADMIN und MODERATOR offen.
 */

// ── Anfragen ──────────────────────────────────────────────────────────────────────────────────

/** Woher eine Anfrage stammt. */
export type InquiryKind = 'CONTACT' | 'PARTNER';

/** Bearbeitungsstand im Posteingang. */
export type InquiryStatus = 'OPEN' | 'IN_PROGRESS' | 'DONE';

/** Ab diesem Wert gilt eine Anfrage als Spam (`InquirySpamCheck.SPAM_THRESHOLD`). */
export const INQUIRY_SPAM_THRESHOLD = 50;

export const INQUIRY_STATUS_LABELS: Record<InquiryStatus, string> = {
  OPEN: 'Offen',
  IN_PROGRESS: 'In Arbeit',
  DONE: 'Erledigt',
};

export const INQUIRY_KIND_LABELS: Record<InquiryKind, string> = {
  CONTACT: 'Kontaktformular',
  PARTNER: 'Für Partner',
};

export interface InquiryAttachment {
  id: number;
  inquiryId: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface Inquiry {
  id: number;
  kind: InquiryKind;
  name: string;
  company?: string | null;
  email: string;
  topic?: string | null;
  message?: string | null;
  status: InquiryStatus;
  formDurationMs?: number | null;
  handledBy?: number | null;
  handledByName?: string | null;
  handledAt?: string | null;
  spamScore: number;
  spamReason?: string | null;
  attachments: InquiryAttachment[];
  createdAt: string;
}

export interface InquiryCounts {
  all: number;
  open: number;
  inProgress: number;
  done: number;
  spam: number;
}

export interface InquiryPage {
  content: Inquiry[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/** Eine Nachricht im Verlauf einer Anfrage, dieselbe Form wie im Outreach-Postfach. */
export type InquiryMessage = Omit<OutreachMessage, 'contactId' | 'templateId' | 'dismissedAt'> & {
  inquiryId: number;
};

/** Worüber geantwortet wird. `enabled = false`: kein CONTACT_FROM konfiguriert. */
export interface InquiryMailbox {
  enabled: boolean;
  address?: string | null;
  inboxActive: boolean;
  lastPolledAt?: string | null;
  lastError?: string | null;
}

export interface InquiryFilter {
  kind?: InquiryKind;
  status?: InquiryStatus;
  spam?: boolean;
  q?: string;
  page?: number;
  size?: number;
}

/** Ausfülldauer lesbar, das Signal hinter einer Spam-Einstufung. */
export function formatFormDuration(ms?: number | null): string {
  if (ms == null) return 'nicht erfasst';
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 90) return `${s.toLocaleString('de-DE', { maximumFractionDigits: 1 })} s`;
  return `${Math.round(s / 60)} min`;
}

// ── Outreach ──────────────────────────────────────────────────────────────────────────────────

export interface RenderedOutreachTemplate {
  subject: string;
  body: string;
}

/** Hat der Kontakt einen Verlauf? Genau das macht ihn zum Chat (Web: `contactBits.hasHistory`). */
export const outreachHasHistory = (c: OutreachContact): boolean =>
  (c.messageCount ?? 0) + (c.noteCount ?? 0) > 0;

/** Der offene Entwurf: bestellt, fertig oder gescheitert (Web: `DraftPanel.openDraftOf`). */
export const openOutreachDraft = (drafts: OutreachDraft[]): OutreachDraft | null =>
  drafts.find(d => d.status === 'REQUESTED' || d.status === 'READY' || d.status === 'FAILED') ?? null;

export function createPosteingangService(api: AxiosInstance) {
  return {
    // Anfragen
    inquiries: async (f: InquiryFilter = {}): Promise<InquiryPage> => {
      const { kind, status, spam, q, page = 0, size = 30 } = f;
      return (await api.get('/inquiries', { params: { kind, status, spam, q: q || undefined, page, size } })).data;
    },
    inquiryCounts: async (f: { kind?: InquiryKind; q?: string } = {}): Promise<InquiryCounts> =>
      (await api.get('/inquiries/counts', { params: { kind: f.kind, q: f.q || undefined } })).data,
    setInquiryStatus: async (id: number, status: InquiryStatus): Promise<Inquiry> =>
      (await api.patch(`/inquiries/${id}/status`, { status })).data,
    setInquirySpam: async (id: number, spam: boolean): Promise<Inquiry> =>
      (await api.post(`/inquiries/${id}/spam`, null, { params: { spam } })).data,
    inquiryMailbox: async (): Promise<InquiryMailbox> => (await api.get('/inquiries/mailbox')).data,
    inquiryMessages: async (id: number): Promise<InquiryMessage[]> =>
      ((await api.get(`/inquiries/${id}/messages`)).data as InquiryMessage[]).map(m => ({ ...m, attachments: m.attachments ?? [] })),
    /** Stellt eine Antwort in die Warteschlange. Ohne Betreff setzt der Server einen passenden. */
    sendInquiryReply: async (id: number, payload: { subject?: string; body: string }): Promise<InquiryMessage> => {
      const m = (await api.post(`/inquiries/${id}/messages`, payload)).data;
      return { ...m, attachments: [] };
    },

    // Outreach: Kontakte und Verlauf
    contacts: async (): Promise<OutreachContact[]> => (await api.get('/admin/outreach/contacts')).data,
    contact: async (id: number): Promise<OutreachContactDetail> => (await api.get(`/admin/outreach/contacts/${id}`)).data,
    createContact: async (data: CreateOutreachContactRequest): Promise<OutreachContact> =>
      (await api.post('/admin/outreach/contacts', data)).data,
    updateContact: async (id: number, data: UpdateOutreachContactRequest): Promise<OutreachContact> =>
      (await api.put(`/admin/outreach/contacts/${id}`, data)).data,
    deleteContact: async (id: number): Promise<void> => { await api.delete(`/admin/outreach/contacts/${id}`); },
    setOwner: async (id: number, ownerUserId: number | null): Promise<OutreachContact> =>
      (await api.put(`/admin/outreach/contacts/${id}/owner`, { ownerUserId })).data,
    setRead: async (id: number, read: boolean): Promise<OutreachContact> =>
      (await api.post(`/admin/outreach/contacts/${id}/${read ? 'read' : 'unread'}`)).data,
    team: async (): Promise<OutreachTeamMember[]> => (await api.get('/admin/outreach/team')).data,
    history: async (id: number): Promise<OutreachMessage[]> => (await api.get(`/admin/outreach/contacts/${id}/messages`)).data,
    notes: async (id: number): Promise<OutreachNote[]> => (await api.get(`/admin/outreach/contacts/${id}/notes`)).data,
    addNote: async (id: number, data: OutreachNoteRequest): Promise<OutreachNote> =>
      (await api.post(`/admin/outreach/contacts/${id}/notes`, data)).data,
    updateNote: async (noteId: number, data: OutreachNoteRequest): Promise<OutreachNote> =>
      (await api.put(`/admin/outreach/notes/${noteId}`, data)).data,
    deleteNote: async (noteId: number): Promise<void> => { await api.delete(`/admin/outreach/notes/${noteId}`); },
    renderTemplate: async (contactId: number, templateId: number): Promise<RenderedOutreachTemplate> =>
      (await api.get(`/admin/outreach/contacts/${contactId}/render/${templateId}`)).data,
    sendEmail: async (contactId: number, data: SendOutreachEmailRequest): Promise<OutreachMessage> =>
      (await api.post(`/admin/outreach/contacts/${contactId}/send`, data)).data,
    signature: async (): Promise<MailSignature> => (await api.get('/admin/outreach/signature')).data,
    saveSignature: async (fields: MailSignatureRequest): Promise<MailSignature> =>
      (await api.put('/admin/outreach/signature', fields)).data,
    mailbox: async (): Promise<OutreachMailbox> => (await api.get('/admin/outreach/mailbox')).data,
    contactFromOrganizer: async (organizerId: number): Promise<OutreachContact> =>
      (await api.post(`/admin/outreach/contacts/from-organizer/${organizerId}`)).data,
    /** Übernimmt eine Anfrage in den Verlauf (Kontakt finden oder anlegen, Text als erste Nachricht). */
    importFromInquiry: async (inquiryId: number): Promise<OutreachContact> =>
      (await api.post(`/admin/outreach/from-inquiry/${inquiryId}`)).data,

    /** Steht beim verknüpften Veranstalter etwas anderes? `null` = keine Verknüpfung. */
    organizerDiff: async (id: number): Promise<OutreachOrganizerDiff | null> =>
      (await api.get(`/admin/outreach/contacts/${id}/organizer-diff`)).data || null,
    applyToOrganizer: async (id: number, fields: string[]): Promise<void> => {
      await api.post(`/admin/outreach/contacts/${id}/apply-to-organizer`, { fields });
    },
    /** Das Verzeichnis mit Kontaktdaten, für „Aus dem Verzeichnis anschreiben". */
    organizers: async (search?: string): Promise<Organizer[]> =>
      (await api.get('/organizers/admin', { params: search ? { search } : undefined })).data,

    // Outreach: Entwürfe des Schreib-Agenten (V173)
    drafts: async (contactId: number): Promise<OutreachDraft[]> =>
      (await api.get(`/admin/outreach/contacts/${contactId}/drafts`)).data,
    requestDraft: async (contactId: number, data: OutreachDraftRequest): Promise<OutreachDraft> =>
      (await api.post(`/admin/outreach/contacts/${contactId}/drafts`, data)).data,
    discardDraft: async (draftId: number): Promise<OutreachDraft> =>
      (await api.post(`/admin/outreach/drafts/${draftId}/discard`)).data,

    // Outreach: Eingänge ohne Kontakt
    unassigned: async (dismissed = false): Promise<OutreachMessage[]> =>
      (await api.get('/admin/outreach/unassigned', { params: { dismissed } })).data,
    assignMessage: async (messageId: number, contactId: number): Promise<OutreachMessage> =>
      (await api.post(`/admin/outreach/messages/${messageId}/assign`, { contactId })).data,
    dismissMessage: async (messageId: number): Promise<OutreachMessage> =>
      (await api.post(`/admin/outreach/messages/${messageId}/dismiss`)).data,
    restoreMessage: async (messageId: number): Promise<OutreachMessage> =>
      (await api.post(`/admin/outreach/messages/${messageId}/restore`)).data,

    // Sperrliste
    suppressions: async (): Promise<OutreachSuppression[]> => (await api.get('/admin/outreach/suppressions')).data,
    removeSuppression: async (email: string): Promise<void> => {
      await api.delete(`/admin/outreach/suppressions/${encodeURIComponent(email)}`);
    },

    // Vorlagen
    templates: async (): Promise<OutreachTemplate[]> => (await api.get('/admin/outreach/templates')).data,
    template: async (id: number): Promise<OutreachTemplate> => (await api.get(`/admin/outreach/templates/${id}`)).data,
    placeholders: async (): Promise<OutreachPlaceholder[]> => (await api.get('/admin/outreach/placeholders')).data,
    createTemplate: async (data: CreateOutreachTemplateRequest): Promise<OutreachTemplate> =>
      (await api.post('/admin/outreach/templates', data)).data,
    updateTemplate: async (id: number, data: UpdateOutreachTemplateRequest): Promise<OutreachTemplate> =>
      (await api.put(`/admin/outreach/templates/${id}`, data)).data,
    deleteTemplate: async (id: number): Promise<void> => { await api.delete(`/admin/outreach/templates/${id}`); },
    removeTemplateAttachment: async (templateId: number, attachmentId: number): Promise<OutreachTemplate> =>
      (await api.delete(`/admin/outreach/templates/${templateId}/attachments/${attachmentId}`)).data,
  };
}

export type PosteingangService = ReturnType<typeof createPosteingangService>;
